import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productService } from './index';
import { toast } from 'react-toastify';

// Get all products
export const useProducts = () => {
  return useQuery({
    queryKey: ['products'],
    queryFn: productService.getAllProducts,
    select: (data) => data.data,
  });
};

// Get products by shop
export const useProductsByShop = (shopId) => {
  return useQuery({
    queryKey: ['products', 'shop', shopId],
    queryFn: () => productService.getProductsByShop(shopId),
    select: (data) => data.data,
    enabled: !!shopId,
  });
};

// Get product by ID
export const useProduct = (id) => {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => productService.getProductById(id),
    select: (data) => data.data,
    enabled: !!id,
  });
};

/** Vendor: default tags + custom tags from their catalog */
export const useVendorTagSuggestions = (userId) => {
  return useQuery({
    queryKey: ['products', 'tag-suggestions', userId],
    queryFn: () => productService.getVendorTagSuggestions().then((r) => r.data),
    enabled: !!userId,
  });
};

// Create product mutation
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productService.createProduct,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'tag-suggestions'] });
      toast.success('Product created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create product');
    },
  });
};

// Update product mutation
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => productService.updateProduct(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'tag-suggestions'] });
      toast.success('Product updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update product');
    },
  });
};

// Delete product mutation
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productService.deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'tag-suggestions'] });
      toast.success('Product deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete product');
    },
  });
};
